import { LEAGUE, type MoneyBoard } from '@ff/core';
import { money } from '../lib/format.ts';

/**
 * The chalkboard pot total: the first thing you see. The pot is the whole
 * reason the league exists, so it opens the board.
 */
export function PotHeader({
  money: board,
  season,
  leagueName,
  live,
  nameOf,
}: {
  money: MoneyBoard;
  season: number;
  /** From ESPN, so a rename there shows up here without a deploy. */
  leagueName: string;
  live?: boolean;
  nameOf?: (id: string) => string;
}) {
  const claimed = board.owners.reduce((sum, o) => sum + o.earned, 0);
  const settled = claimed > 0 ? Math.round((claimed / board.pot) * 100) : 0;
  const managers = board.owners.length;
  const owing = board.owners.filter((o) => !o.boughtIn);
  // Derive the buy-in figures from the owners rather than the board's top-level
  // fields, which are absent on season docs cached before buy-in tracking
  // existed. A cached owner has no `boughtIn` flag either, so this reads 0 in /
  // 0 bought in (the correct "not yet recorded" default) instead of $NaN.
  const boughtInCount = managers - owing.length;
  const buyInsCollected = boughtInCount * board.config.buyIn;

  return (
    <header className="relative overflow-hidden">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="flex items-center gap-3">
            <p className="eyebrow">{leagueName} · {season}</p>
            {live && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-blood/15 px-2 py-0.5 font-mono text-[0.65rem] uppercase tracking-wider text-blood">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-blood" />
                Live
              </span>
            )}
          </div>
          <h1 className="mt-2 font-display text-4xl font-black uppercase leading-none tracking-tight sm:text-5xl">
            {LEAGUE.bookName}
          </h1>
          <p className="mt-3 max-w-md text-sm text-chalk-dim">
            {managers} managers, one pot, and a full season of
            receipts. Ranked by what you're up, not your record.
          </p>
        </div>

        <div className="text-right">
          <p className="eyebrow">The Pot</p>
          <p className="font-mono text-5xl font-bold tabular text-brass sm:text-6xl">
            {money(board.pot)}
          </p>
          <p className="mt-1 font-mono text-xs text-chalk-dim tabular">
            {money(buyInsCollected)} in · {boughtInCount} of {managers} bought in
          </p>
          {claimed > 0 && (
            <p className="font-mono text-xs text-chalk-dim tabular">
              {money(claimed)} paid out · {settled}%
            </p>
          )}
          {/* A gentle nudge once collection is under way but not everyone's in. */}
          {boughtInCount > 0 && owing.length > 0 && owing.length <= 4 && nameOf && (
            <p className="mt-1 font-mono text-[0.7rem] text-blood/80">
              still owes: {owing.map((o) => nameOf(o.ownerId)).join(', ')}
            </p>
          )}
        </div>
      </div>

      {/* A chalk rule under the header. */}
      <div className="mt-6 h-px w-full bg-felt-line" />
    </header>
  );
}
